
import { defineComponent, onBeforeMount, reactive } from 'vue'
import ymaps from 'yandex-maps'

import { YandexFeatureMemberT } from '@/core/types/geocoder.types'
import { geocoder } from '@/core/realisations/geocoder.realisation'

import NewMap from '@/common/new-components/NewMap/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

import useMap from '@/common/new-composable/useMap'
import useMapGeoObjects from '@/common/new-composable/useMapGeoObjects'

type SavedFeatureMember = {
    name: string
    description: string
    coordinates: number[]
}

const LOCAL_STORAGE_KEY = 'spark-bpms-nearby-localities'

export default defineComponent({
    components: {
        NewMap,
        TheButton
    },
    setup() {
        let map_instance = null as ymaps.Map | null

        const map_graphics = useMapGeoObjects()
        const { addGeoObject, clearMap, setMapCenter } = useMap()

        const state = reactive({
            is_loading: false,
            is_rectangle_created: false,
            is_click_blocked: false,
            range_in_km: 5,
            center: [] as number[],
            featureMember: [] as YandexFeatureMemberT[],
            savedFeatureMember: [] as SavedFeatureMember[],
            skip: null as number | null,
        })

        const createPlacemark = (coordinates: number[]) => {
            const placemark = map_graphics.createPoint(
                coordinates,
                {
                    iconContent: 'Центр поиска',
                },
                {
                    preset: 'islands#blueStretchyIcon',
                }
            )

            addGeoObject(map_instance as ymaps.Map, placemark)
        }

        const onMapReady = (instance: ymaps.Map) => {
            map_instance = instance
            renderSavedFeatureMember()
        }

        const onMapClick = (
            event: MouseEvent | ymaps.IEvent<Record<any, any>, Record<any, any>>
        ) => {
            if (state.is_loading || state.is_click_blocked) return

            // @ts-ignore
            const coordinates = event.get('coords')
            state.center = coordinates
            setMapCenter(map_instance as ymaps.Map, coordinates)

            state.featureMember = []
            state.skip = null

            rerenderObjects()
        }

        const createRectangle = (center: number[]) => {
            createPlacemark(center)

            const latDelta = state.range_in_km / 111
            const lonDelta = state.range_in_km / (111 * Math.cos(center[0] * Math.PI / 180))

            const topLeft = [center[0] + latDelta / 2, center[1] - lonDelta / 2]
            const bottomRight = [center[0] - latDelta / 2, center[1] + lonDelta / 2]

            // @ts-ignore
            const rectangle = new window.ymaps.Rectangle(
                [topLeft, bottomRight],
                {
                    hintContent: 'Область поиска',
                },
                {
                    draggable: false,
                    fillColor: '#33A4FF',
                    fillOpacity: 0.2,
                    strokeColor: '#297ABC',
                    strokeWidth: 1,
                    interactivityModel: 'default#transparent'
                }
            )

            addGeoObject(map_instance as ymaps.Map, rectangle)
            state.is_rectangle_created = true
        }

        const onIncreaseRange = () => {
            if (state.is_loading) return
            state.range_in_km += 1
            rerenderObjects()
        }

        const onDecreaseRange = () => {
            if (state.is_loading) return
            state.range_in_km -= 1
            rerenderObjects()
        }

        const rerenderObjects = () => {
            clearMap(map_instance as ymaps.Map)
            state.is_rectangle_created = false
            createRectangle(state.center)
            renderFeatureMember()
            renderSavedFeatureMember()
        }

        const calculateSpn = (): string => {
            const latDelta = state.range_in_km / 111 // 1° широты = ~111 км
            const lonDelta = state.range_in_km / (111 * Math.cos(state.center[0] * Math.PI / 180)) // Учитываем широту
            return `${lonDelta.toFixed(6)},${latDelta.toFixed(6)}`
        }

        const formatCoordinatesFromString = (coords_string: string): number[] => {
            const coords = coords_string.split(' ')
            const longitude = Number(coords[0])
            const latitude = Number(coords[1])

            return [latitude, longitude]
        }

        const setMapCenterByPosCoordinates = (name: string, coords_string: string) => {
            const coordinates = formatCoordinatesFromString(coords_string)
            setMapCenter(map_instance as ymaps.Map, coordinates)
        }

        const setMapCenterByCoordinates = (coordinates: number[]) => {
            setMapCenter(map_instance as ymaps.Map, coordinates)
        }

        const geocodeLocation = async () => {
            if (state.is_loading || !state.is_rectangle_created) return

            try {
                state.is_loading = true

                const response = await geocoder.geocode({
                    geocode: [...state.center].reverse().join(','),
                    kind: 'locality',
                    results: 50,
                    ll: [...state.center].reverse().join(','),
                    spn: calculateSpn(),
                    skip: state.skip
                })

                state.featureMember = response.response.GeoObjectCollection.featureMember
                rerenderObjects()
            } catch (error) {
                console.error(error)
            } finally {
                state.is_loading = false
            }
        }

        const syncLocalStorage = () => {
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state.savedFeatureMember))
        }

        const clearLocalStorage = () => {
            localStorage.removeItem(LOCAL_STORAGE_KEY)
        }

        const saveAllFeatureMember = () => {
            if (state.featureMember.length === 0) return

            const savedFeatureMember: SavedFeatureMember[] = state.featureMember.map((featureMember) => {
                return {
                    name: featureMember.GeoObject.name,
                    description: featureMember.GeoObject.description,
                    coordinates: formatCoordinatesFromString(featureMember.GeoObject.Point.pos)
                }
            })

            const combined = state.savedFeatureMember.concat(savedFeatureMember)
            state.savedFeatureMember = [...combined]
            state.featureMember = []

            syncLocalStorage()

            rerenderObjects()
        }

        const saveFeatureMember = (featureMember: YandexFeatureMemberT, index: number) => {
            const savedFeatureMember = {
                name: featureMember.GeoObject.name,
                description: featureMember.GeoObject.description,
                coordinates: formatCoordinatesFromString(featureMember.GeoObject.Point.pos)
            }

            state.savedFeatureMember.push(savedFeatureMember)
            state.featureMember.splice(index, 1)

            syncLocalStorage()
            rerenderObjects()
        }

        const deleteSavedFeatureMember = (index: number) => {
            const answer = confirm(`Вы действительно хотите удалить запись "${state.savedFeatureMember[index].name}"?\n${state.savedFeatureMember[index].description}`)
            if (!answer) return

            state.savedFeatureMember.splice(index, 1)
            syncLocalStorage()

            rerenderObjects()
        }

        const clearSavedFeatureMember = () => {
            const answer = confirm('Вы действительно хотите очистить сохраненные данные?')
            if (!answer) return

            state.savedFeatureMember = []
            clearLocalStorage()
            rerenderObjects()
        }

        const saveJson = () => {
            const jsonData = JSON.stringify(state.savedFeatureMember, null, 2)

            const blob = new Blob([jsonData], { type: 'application/json' })
            const url = URL.createObjectURL(blob)

            const a = document.createElement('a')
            a.href = url
            a.download = 'nearby-localities.json'
            a.click()

            URL.revokeObjectURL(url)
        }

        const intializeLocalStorage = () => {
            const items = localStorage.getItem(LOCAL_STORAGE_KEY)
            
            if (items) {
                state.savedFeatureMember = JSON.parse(items)
            }
        }

        onBeforeMount(() => {
            intializeLocalStorage()
        })

        const onIncreaseSkip = () => {
            if (state.is_loading) return
            
            if (!state.skip) {
                state.skip = 50
                geocodeLocation()
                return
            }

            state.skip += 50
            geocodeLocation()
        }

        const onDecresaseSkip = () => {
            if (state.is_loading || !state.skip) return

            if (state.skip === 50) {
                state.skip = null
            } else {
                state.skip -= 50
            }
            
            geocodeLocation()
        }

        const renderFeatureMember = () => {
            if (state.featureMember.length === 0) return

            state.featureMember.forEach((featureMember) => {
                const distanceInKm = calculateDistance(formatCoordinatesFromString(featureMember.GeoObject.Point.pos), state.center)

                const placemark = map_graphics.createPoint(
                    formatCoordinatesFromString(featureMember.GeoObject.Point.pos),
                    {
                        iconContent: `${featureMember.GeoObject.name} (${distanceInKm.toFixed(2) || '-'} км)`,
                    },
                    {
                        preset: 'islands#darkBlueStretchyIcon',
                    }
                )

                addGeoObject(map_instance as ymaps.Map, placemark)
            })
        }

        const renderSavedFeatureMember = () => {
            if (state.savedFeatureMember.length === 0) return

            state.savedFeatureMember.forEach((savedFeatureMember) => {
                const distanceInKm = calculateDistance(savedFeatureMember.coordinates, state.center)

                const placemark = map_graphics.createPoint(
                    savedFeatureMember.coordinates,
                    {
                        iconContent: `${savedFeatureMember.name} (${distanceInKm.toFixed(2) || '-'} км)`,
                    },
                    {
                        preset: 'islands#greenStretchyIcon',
                    }
                )

                addGeoObject(map_instance as ymaps.Map, placemark)
            })
        }

        const calculateDistance = (coordinates1: number[], coordinates2: number[]): number => {
            if (coordinates2.length === 0) {
                return 0
            }

            const lat1 = coordinates1[0]
            const lon1 = coordinates1[1]
            const lat2 = coordinates2[0]
            const lon2 = coordinates2[1]

            const R = 6371

            const toRadians = (degrees: number): number => degrees * (Math.PI / 180)

            const φ1 = toRadians(lat1)
            const φ2 = toRadians(lat2)
            const Δφ = toRadians(lat2 - lat1)
            const Δλ = toRadians(lon2 - lon1)

            const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
                    Math.cos(φ1) * Math.cos(φ2) *
                    Math.sin(Δλ / 2) * Math.sin(Δλ / 2)

            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

            return R * c
        }

        const toggleClick = () => {
            state.is_click_blocked = !state.is_click_blocked
        }

        return {
            state,
            onMapReady,
            onMapClick,
            onIncreaseRange,
            onDecreaseRange,
            geocodeLocation,
            saveAllFeatureMember,
            saveFeatureMember,
            setMapCenterByPosCoordinates,
            setMapCenterByCoordinates,
            clearSavedFeatureMember,
            deleteSavedFeatureMember,
            saveJson,
            onIncreaseSkip,
            onDecresaseSkip,
            toggleClick,
        }
    }
})
